/**
 * "PROVE ONCE, SATISFY MANY" — the coverage view.
 *
 * The product's differentiator in one screen: a control you do once is
 * recognised everywhere it is mapped. Two panels answer the two questions a
 * CISO brings to it:
 *
 *   1. "How far along am I, per framework, thanks to controls already in place?"
 *   2. "What should I do next to move the most frameworks at once?"
 *
 * ---------------------------------------------------------------------------
 *  HOW TO READ THE NUMBERS (and what they refuse to be)
 * ---------------------------------------------------------------------------
 * The percentage is a plain count: distinct framework requirements discharged
 * by a FULL mapping from a control marked SATISFIED, over the framework's
 * total requirements. No weights. PARTIAL mappings are never folded in — they
 * are stated separately ("also partly supported") because a partial citation
 * by definition does not discharge the requirement.
 *
 * The bar has three layers so that "ticked" is never mistaken for "proved":
 *   solid green   — fully covered by satisfied controls that have evidence
 *   amber         — fully covered by satisfied controls with NO evidence yet
 *   grey shadow   — the ceiling: what the catalogue could ever cover, so a low
 *                   bar can be told apart from a framework the catalogue only
 *                   maps thinly.
 *
 * Colour: the status ramp carries meaning (good / warning); framework identity
 * is the categorical mark (FrameworkMark), never the ramp.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ControlCoverage, OrgControlStatus } from '@opencomply/shared';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { Card, EmptyState, ErrorNotice, Pill, Skeleton, type Tone } from './ui';
import { FrameworkMark } from './FrameworkMark';

export const COVERAGE_STRINGS = {
  'coverage.tab.coverage': 'Coverage',
  'coverage.tab.controls': 'All controls',
  'coverage.title': 'Prove once, satisfy many',
  'coverage.subtitle':
    'Each control you do counts toward every framework it is mapped to. This is how far the controls you already have in place take you, and which ones to do next.',
  'coverage.loadFailed': 'Could not load the coverage view.',
  'coverage.empty': 'The control catalogue has not been seeded yet.',

  'coverage.headline': '{{inPlace}} of {{total}} catalogue controls in place',
  'coverage.headlineProven': '{{proven}} with evidence',

  'coverage.frameworks.title': 'Coverage by framework',
  'coverage.frameworks.hint':
    'Share of each framework’s requirements fully discharged by controls you have marked satisfied. Partly-supported requirements are counted separately, never added in.',
  'coverage.fullOf': '{{full}} of {{total}} requirements fully covered',
  'coverage.partialAlso': '{{count}} more partly supported',
  'coverage.ceiling': 'Catalogue can reach up to {{percent}}%',
  'coverage.controlsInPlace': '{{done}} of {{mapped}} mapped controls in place',
  'coverage.viaShared': '{{percent}}% covered via shared controls',
  'coverage.nothingYet': 'Nothing in place yet',
  'coverage.legend.proven': 'Covered, with evidence',
  'coverage.legend.unproven': 'Covered, no evidence yet',
  'coverage.legend.ceiling': 'Catalogue ceiling',
  'coverage.unprovenWarn':
    '{{count}} of the covered requirements rest on controls marked satisfied with no evidence attached.',

  'coverage.next.title': 'Do these next',
  'coverage.next.hint':
    'Controls not yet in place, ordered by how many frameworks each one newly moves. Each is done once and recorded once.',
  'coverage.next.frameworks_one': 'Moves {{count}} framework forward',
  'coverage.next.frameworks_other': 'Moves {{count}} frameworks forward',
  'coverage.next.requirements_one': '{{count}} new requirement fully covered',
  'coverage.next.requirements_other': '{{count}} new requirements fully covered',
  'coverage.next.alsoPartial': 'partial',
  'coverage.next.more': '+{{count}} more',
  'coverage.next.open': 'Open control',
  'coverage.next.none': 'Every control that can add coverage is already in place.',
  'coverage.next.showAll': 'Showing the top {{shown}} of {{total}} controls not yet in place.',
  'coverage.status.NOT_ASSESSED': 'Not assessed yet',
  'coverage.status.SATISFIED': 'Satisfied',
  'coverage.status.PARTIAL': 'Partly satisfied',
  'coverage.status.NOT_SATISFIED': 'Not satisfied',
  'coverage.status.NOT_APPLICABLE': 'Not applicable',
} as const;

export type CoverageKey = keyof typeof COVERAGE_STRINGS;

interface Tier { full: number; partialOnly: number; fullPercent: number }

interface FrameworkCoverage {
  key: string;
  name: string;
  version: string;
  totalRequirements: number;
  reachable: Tier;
  satisfied: Tier;
  proven: Tier;
  controlsMapped: number;
  controlsSatisfied: number;
}

interface CoverageControl {
  key: string;
  title: string;
  domain: string | null;
  status: OrgControlStatus;
  evidenceCount: number;
  inPlace: boolean;
  frameworkCount: number;
  requirementCount: number;
  fullCount: number;
  partialCount: number;
  frameworks: Array<{
    key: string;
    name: string;
    full: number;
    partial: number;
    refs: Array<{ ref: string; coverage: ControlCoverage }>;
  }>;
  unlocks: { fullRequirements: number; frameworkCount: number };
}

interface CoverageResponse {
  frameworks: FrameworkCoverage[];
  controls: CoverageControl[];
  totals: { controls: number; controlsInPlace: number; controlsProven: number };
}

const STATUS_TONE: Record<OrgControlStatus, Tone> = {
  NOT_ASSESSED: 'neutral',
  SATISFIED: 'good',
  PARTIAL: 'warning',
  NOT_SATISFIED: 'critical',
  NOT_APPLICABLE: 'neutral',
};

const NEXT_LIMIT = 10;
const REFS_SHOWN = 4;

function useCoverageStrings() {
  const { t } = useTranslation();
  return (key: CoverageKey, options?: Record<string, unknown>) =>
    t(key, { defaultValue: COVERAGE_STRINGS[key], ...options });
}

/** Plural-aware: i18next picks the suffix, we pick the inline fallback. */
function useCoveragePlural() {
  const { t } = useTranslation();
  return (base: 'coverage.next.frameworks' | 'coverage.next.requirements', count: number) =>
    t(base, {
      count,
      defaultValue: COVERAGE_STRINGS[`${base}_${count === 1 ? 'one' : 'other'}` as CoverageKey],
    });
}

// ---------------------------------------------------------------------------
//  (a) Per-framework coverage
// ---------------------------------------------------------------------------

function FrameworkCoverageRow({ fw }: { fw: FrameworkCoverage }) {
  const tx = useCoverageStrings();
  const total = Math.max(1, fw.totalRequirements);
  const pct = (n: number) => Math.min(100, (n / total) * 100);

  const provenFull = fw.proven.full;
  const unprovenFull = Math.max(0, fw.satisfied.full - fw.proven.full);
  const ceiling = fw.reachable.full;
  const nothing = fw.satisfied.full === 0 && fw.satisfied.partialOnly === 0;

  return (
    <li className="space-y-2">
      <div className="flex items-center gap-3">
        <FrameworkMark frameworkKey={fw.key} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink truncate">{fw.name}</p>
          <p className="text-xs text-ink-muted">
            {nothing
              ? tx('coverage.nothingYet')
              : tx('coverage.viaShared', { percent: fw.satisfied.fullPercent })}
          </p>
        </div>
        <p className="text-2xl font-semibold tabular-nums text-ink">
          {fw.satisfied.fullPercent}
          <span className="text-base text-ink-muted">%</span>
        </p>
      </div>

      <div
        role="progressbar"
        aria-valuenow={fw.satisfied.fullPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${fw.name}: ${tx('coverage.viaShared', { percent: fw.satisfied.fullPercent })}`}
        className="relative h-3 rounded-full bg-ink/[0.07] overflow-hidden"
      >
        {/* The ceiling: a darker shadow behind the fill, out to what the
            catalogue could cover if every control were done. */}
        <div
          className="absolute inset-y-0 left-0 bg-ink/[0.10]"
          style={{ width: `${pct(ceiling)}%` }}
        />
        <div className="absolute inset-0 flex">
          <div
            className="h-full bg-good transition-[width] duration-500"
            style={{ width: `${pct(provenFull)}%` }}
          />
          <div
            className="h-full bg-warning transition-[width] duration-500"
            style={{ width: `${pct(unprovenFull)}%` }}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-muted">
        <span className="text-ink-secondary">
          {tx('coverage.fullOf', { full: fw.satisfied.full, total: fw.totalRequirements })}
        </span>
        {fw.satisfied.partialOnly > 0 && (
          <span>{tx('coverage.partialAlso', { count: fw.satisfied.partialOnly })}</span>
        )}
        <span>{tx('coverage.controlsInPlace', { done: fw.controlsSatisfied, mapped: fw.controlsMapped })}</span>
        <span>{tx('coverage.ceiling', { percent: fw.reachable.fullPercent })}</span>
      </div>
    </li>
  );
}

function FrameworkCoveragePanel({ frameworks }: { frameworks: FrameworkCoverage[] }) {
  const tx = useCoverageStrings();
  const unproven = frameworks.reduce(
    (sum, f) => sum + Math.max(0, f.satisfied.full - f.proven.full),
    0,
  );

  return (
    <Card className="p-4 sm:p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-ink">{tx('coverage.frameworks.title')}</h2>
        <p className="text-xs text-ink-muted mt-0.5 max-w-3xl">{tx('coverage.frameworks.hint')}</p>
      </div>

      <ul className="space-y-5">
        {frameworks.map((fw) => <FrameworkCoverageRow key={fw.key} fw={fw} />)}
      </ul>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted" aria-hidden>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-good" />{tx('coverage.legend.proven')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-warning" />{tx('coverage.legend.unproven')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-ink/[0.10]" />{tx('coverage.legend.ceiling')}
        </span>
      </div>

      {unproven > 0 && (
        <p className="text-xs text-warning bg-warning/10 rounded-md p-2">
          {tx('coverage.unprovenWarn', { count: unproven })}
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  (b) Highest-leverage controls to do next
// ---------------------------------------------------------------------------

function NextControlRow({ control, rank }: { control: CoverageControl; rank: number }) {
  const tx = useCoverageStrings();
  const txn = useCoveragePlural();

  return (
    <li>
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="w-7 h-7 shrink-0 rounded-full bg-brand/10 text-brand grid place-items-center text-xs font-semibold tabular-nums"
          >
            {rank}
          </span>

          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <Link
                  to={`/catalogue/${encodeURIComponent(control.key)}`}
                  className="text-sm font-semibold text-ink hover:text-brand hover:underline"
                >
                  {control.title}
                </Link>
                <p className="text-xs font-medium text-brand mt-0.5">
                  {txn('coverage.next.frameworks', control.unlocks.frameworkCount)}
                  {' · '}
                  {txn('coverage.next.requirements', control.unlocks.fullRequirements)}
                </p>
              </div>
              <Pill tone={STATUS_TONE[control.status]}>{tx(`coverage.status.${control.status}`)}</Pill>
            </div>

            {/* One line per framework it touches: mark + the citations. */}
            <ul className="space-y-1.5">
              {control.frameworks.map((fw) => {
                const shown = fw.refs.slice(0, REFS_SHOWN);
                const hidden = fw.refs.length - shown.length;
                return (
                  <li key={fw.key} className="flex items-start gap-2 text-xs">
                    <FrameworkMark frameworkKey={fw.key} size="sm" className="!w-6 !h-6 !rounded-md mt-px" />
                    <span className="min-w-0 pt-0.5 text-ink-secondary">
                      <span className="font-medium text-ink">{fw.name}</span>
                      {' '}
                      {shown.map((r, i) => (
                        <span key={`${r.ref}-${r.coverage}`}>
                          {i > 0 && ', '}
                          {r.coverage === 'PARTIAL' ? (
                            <span className="italic text-ink-muted">
                              {r.ref} ({tx('coverage.next.alsoPartial')})
                            </span>
                          ) : (
                            <span className="font-mono">{r.ref}</span>
                          )}
                        </span>
                      ))}
                      {hidden > 0 && (
                        <span className="text-ink-muted"> {tx('coverage.next.more', { count: hidden })}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </Card>
    </li>
  );
}

function NextControlsPanel({ controls }: { controls: CoverageControl[] }) {
  const tx = useCoverageStrings();
  const candidates = controls.filter(
    (c) => !c.inPlace && c.status !== 'NOT_APPLICABLE' && c.unlocks.fullRequirements > 0,
  );
  const shown = candidates.slice(0, NEXT_LIMIT);

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-ink">{tx('coverage.next.title')}</h2>
        <p className="text-xs text-ink-muted mt-0.5 max-w-3xl">{tx('coverage.next.hint')}</p>
      </div>

      {shown.length === 0 ? (
        <p className="oc-muted">{tx('coverage.next.none')}</p>
      ) : (
        <>
          <ol className="space-y-3">
            {shown.map((c, i) => <NextControlRow key={c.key} control={c} rank={i + 1} />)}
          </ol>
          {candidates.length > shown.length && (
            <p className="text-xs text-ink-muted">
              {tx('coverage.next.showAll', { shown: shown.length, total: candidates.length })}
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
//  The view
// ---------------------------------------------------------------------------

export function CoverageView() {
  const tx = useCoverageStrings();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalogue', 'coverage'],
    queryFn: () => api.get<CoverageResponse>('/catalogue/coverage'),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }
  if (isError || !data) {
    return <ErrorNotice message={tx('coverage.loadFailed')} onRetry={() => void refetch()} />;
  }
  if (data.controls.length === 0) {
    return <EmptyState title={tx('coverage.empty')} />;
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="oc-h2">{tx('coverage.title')}</h2>
        <p className="oc-muted max-w-3xl">{tx('coverage.subtitle')}</p>
        <p className={cn('text-sm font-medium text-ink pt-1')}>
          {tx('coverage.headline', { inPlace: data.totals.controlsInPlace, total: data.totals.controls })}
          <span className="text-ink-muted font-normal">
            {' · '}
            {tx('coverage.headlineProven', { proven: data.totals.controlsProven })}
          </span>
        </p>
      </div>

      <FrameworkCoveragePanel frameworks={data.frameworks} />
      <NextControlsPanel controls={data.controls} />
    </div>
  );
}
